import React from 'react';
import { Eye, Pencil, Trash, Send, BellRing } from 'lucide-react';
import './RecurringInvoicesPopup.css';

const RecurringInvoicesPopup = ({
  show,
  onClose,
  selectedRecurringInvoice,
  clients,
  recurringChildren,
  loadingChildren,
  updatingStatus,
  sendingInvoice,
  sendingReminder,
  isInvoiceOverdue,
  onInvoiceClick,
  onStatusUpdate,
  onSendInvoiceEmail,
  onSendReminder,
  onDeleteInvoice,
  onEditInvoice
}) => {
  if (!show) return null;

  const StatusToggle = ({ invoice }) => (
    <button
      type="button"
      className={`recurring-status-toggle ${invoice.status === 'Paid' ? 'paid' : 'unpaid'} ${updatingStatus[invoice.id] || !invoice.email_sent ? 'disabled' : ''}`}
      onClick={() => {
        if (invoice.email_sent && !updatingStatus[invoice.id]) {
          onStatusUpdate(invoice.id, invoice.status === 'Paid' ? 'Unpaid' : 'Paid');
        }
      }}
      disabled={updatingStatus[invoice.id] || !invoice.email_sent}
      title={!invoice.email_sent ? 'Invoice must be sent before status can be changed' : ''}
    >
      <div className="recurring-toggle-knob"></div>
      <span className="recurring-status-text">
        {updatingStatus[invoice.id] ? 'Updating...' : !invoice.email_sent ? 'Not Sent' : invoice.status || 'Unpaid'}
      </span>
    </button>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="recurring-popup-overlay" onClick={onClose}>
      <div className="recurring-popup" onClick={(e) => e.stopPropagation()}>
        <div className="recurring-popup-header">
          <div className="recurring-popup-title-section">
            <h2 className="recurring-popup-title">
              Recurring Invoice Instances
            </h2>
            <div className="recurring-parent-info">
              <p><strong>Invoice:</strong> {selectedRecurringInvoice?.invoice_number}</p>
              <p><strong>Client:</strong> {clients[selectedRecurringInvoice?.client_id] || 'Unknown Client'}</p>
              <p><strong>Interval:</strong> {selectedRecurringInvoice?.recurring_interval}</p>
              <p><strong>End Date:</strong> {selectedRecurringInvoice?.recurring_end || 'No end date'}</p>
            </div>
          </div>
          <button 
            className="recurring-popup-close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        
        <div className="recurring-popup-content">
          {loadingChildren ? (
            <div className="recurring-popup-loading">
              <div className="recurring-loading-spinner" />
              <p>Loading child invoices...</p>
            </div>
          ) : recurringChildren.length > 0 ? (
            <>
              {/* Desktop Table View */}
              <div className="recurring-table-view">
                <div className="recurring-table-wrapper">
                  <table className="recurring-table">
                    <thead className="recurring-table-header">
                      <tr>
                        <th>Invoice #</th>
                        <th>Amount</th>
                        <th>Issue Date</th>
                        <th>Status</th>
                        <th>Due Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="recurring-table-body">
                      {recurringChildren.map(child => (
                        <tr key={child.id} className="recurring-table-row">
                          <td className="recurring-table-cell">
                            <span className="recurring-cell-content">{child.invoice_number || 'Unnamed'}</span>
                          </td>
                          <td className="recurring-table-cell">
                            <span className="recurring-amount">₹{parseFloat(child.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                          </td>
                          <td className="recurring-table-cell">
                            <span className="recurring-cell-content">
                              {formatDate(child.issue_date)}
                            </span>
                          </td>
                          <td className="recurring-table-cell">
                            <div className="recurring-status-container">
                              <StatusToggle invoice={child} />
                            </div>
                          </td>
                          <td className="recurring-table-cell">
                            <span className="recurring-cell-content">
                              {formatDate(child.due_date)}
                            </span>
                          </td>
                          <td className="recurring-table-cell">
                            <div className="recurring-actions-container">
                              <button
                                className="recurring-action-btn recurring-view-btn"
                                onClick={() => onInvoiceClick(child)}
                                title="View Invoice"
                              >
                                <Eye size={14} className="recurring-icon" />
                                <span className="recurring-btn-text"></span>
                              </button>
                              
                              {!child.email_sent ? (
                                <>
                                  <button 
                                    className="recurring-action-btn recurring-edit-btn" 
                                    onClick={() => onEditInvoice && onEditInvoice(child)}
                                    title="Edit Invoice"
                                  >
                                    <Pencil size={14} className="recurring-icon" />
                                    <span className="recurring-btn-text"></span>
                                  </button>
                                  
                                  <button
                                    className="recurring-action-btn recurring-send-btn"
                                    onClick={() => onSendInvoiceEmail(child)}
                                    disabled={sendingInvoice[child.id]}
                                    title="Send Invoice"
                                  >
                                    {sendingInvoice[child.id] ? (
                                      <div className="recurring-spinner"></div>
                                    ) : (
                                      <>
                                        <Send size={14} className="recurring-icon" />
                                        <span className="recurring-btn-text"></span>
                                      </>
                                    )}
                                  </button>
                                  
                                  <button 
                                    className="recurring-action-btn recurring-delete-btn" 
                                    onClick={() => onDeleteInvoice(child, false)}
                                    title="Delete Invoice"
                                  >
                                    <Trash size={14} className="recurring-icon" />
                                    <span className="recurring-btn-text"></span>
                                  </button>
                                </>
                              ) : (
                                <>
                                  {child.status === 'Unpaid' && (
                                    <button
                                      className="recurring-action-btn recurring-reminder-btn"
                                      onClick={() => onSendReminder(child)}
                                      disabled={sendingReminder[child.id]}
                                      title="Send Reminder"
                                    >
                                      {sendingReminder[child.id] ? (
                                        <div className="recurring-spinner"></div>
                                      ) : (
                                        <>
                                          <BellRing size={14} className="recurring-icon" />
                                          <span className="recurring-btn-text"></span>
                                        </>
                                      )}
                                    </button>
                                  )}
                                  
                                  <button
                                    className="recurring-action-btn recurring-delete-btn"
                                    onClick={() => {
                                      const choice = window.prompt(
                                        'Type:\n1 to Add to Income & Delete\n2 to Delete Only',
                                        '1'
                                      );
                                      if (choice === '1') onDeleteInvoice(child, true);
                                      else if (choice === '2') onDeleteInvoice(child, false);
                                    }}
                                    title="Delete Invoice"
                                  >
                                    <Trash size={14} className="recurring-icon" />
                                    <span className="recurring-btn-text"></span>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="recurring-card-view">
                {recurringChildren.map(child => (
                  <div key={child.id} className="recurring-card">
                    <div className="recurring-card-header">
                      <div className="recurring-card-title">
                        <h4>{child.invoice_number || 'Unnamed'}</h4>
                        <span className="recurring-card-amount">
                          ₹{parseFloat(child.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="recurring-card-status">
                        <StatusToggle invoice={child} />
                      </div>
                    </div>
                    
                    <div className="recurring-card-body">
                      <div className="recurring-card-field">
                        <span className="recurring-card-label">Issue Date</span>
                        <span className="recurring-card-value">
                          {formatDate(child.issue_date)}
                        </span>
                      </div>
                      <div className="recurring-card-field">
                        <span className="recurring-card-label">Due Date</span>
                        <span className="recurring-card-value">
                          {formatDate(child.due_date)}
                        </span>
                      </div>
                    </div>

                    <div className="recurring-card-actions">
                      <button
                        className="recurring-action-btn recurring-view-btn"
                        onClick={() => onInvoiceClick(child)}
                        title="View Invoice"
                      >
                        <Eye size={14} className="recurring-icon" />
                        <span className="recurring-btn-text">View</span>
                      </button>
                      
                      {!child.email_sent ? (
                        <>
                          <button 
                            className="recurring-action-btn recurring-edit-btn" 
                            onClick={() => onEditInvoice && onEditInvoice(child)}
                            title="Edit Invoice"
                          >
                            <Pencil size={14} className="recurring-icon" />
                            <span className="recurring-btn-text">Edit</span>
                          </button>
                          
                          <button
                            className="recurring-action-btn recurring-send-btn"
                            onClick={() => onSendInvoiceEmail(child)}
                            disabled={sendingInvoice[child.id]}
                            title="Send Invoice"
                          >
                            {sendingInvoice[child.id] ? (
                              <div className="recurring-spinner"></div>
                            ) : (
                              <>
                                <Send size={14} className="recurring-icon" />
                                <span className="recurring-btn-text">Send</span>
                              </>
                            )}
                          </button>
                          
                          <button 
                            className="recurring-action-btn recurring-delete-btn" 
                            onClick={() => onDeleteInvoice(child, false)}
                            title="Delete Invoice"
                          >
                            <Trash size={14} className="recurring-icon" />
                            <span className="recurring-btn-text">Delete</span>
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            className="recurring-action-btn recurring-edit-btn" 
                            onClick={() => onEditInvoice && onEditInvoice(child)}
                            title="Edit Invoice"
                          >
                            <Pencil size={14} className="recurring-icon" />
                            <span className="recurring-btn-text">Edit</span>
                          </button>

                          {child.status === 'Unpaid' && (
                            <button
                              className="recurring-action-btn recurring-reminder-btn"
                              onClick={() => onSendReminder(child)}
                              disabled={sendingReminder[child.id]}
                              title="Send Reminder"
                            >
                              {sendingReminder[child.id] ? (
                                <div className="recurring-spinner"></div>
                              ) : (
                                <>
                                  <BellRing size={14} className="recurring-icon" />
                                  <span className="recurring-btn-text">Remind</span>
                                </>
                              )}
                            </button>
                          )}
                          
                          <button
                            className="recurring-action-btn recurring-delete-btn"
                            onClick={() => {
                              const choice = window.prompt(
                                'Type:\n1 to Add to Income & Delete\n2 to Delete Only',
                                '1'
                              );
                              if (choice === '1') onDeleteInvoice(child, true);
                              else if (choice === '2') onDeleteInvoice(child, false);
                            }}
                            title="Delete Invoice"
                          >
                            <Trash size={14} className="recurring-icon" />
                            <span className="recurring-btn-text">Delete</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="recurring-empty-state">
              <div className="recurring-empty-icon">📋</div>
              <h3 className="recurring-empty-title">No Child Invoices Yet</h3>
              <p className="recurring-empty-description">
                No child invoices have been generated yet for this recurring invoice.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecurringInvoicesPopup;